import { useEffect, useRef } from "react";
import { useStreamReveal } from "../../hooks/useStreamReveal";
import { Markdown } from "../Markdown";
import { Text } from "../Text";
import { MarkdownStyles } from "./MarkdownStyles";

type Props = {
  text: string;
  /** True while the entry is being actively streamed via SSE. */
  streaming?: boolean;
  onAnimatingChange?: (animating: boolean) => void;
  /** Render plain text instead of Markdown. */
  plain?: boolean;
  variant?: "text-regular" | "text-small";
  color?: string;
  resolveFileHref?: (href: string) => string | null;
};

export function TypewriterText({
  text,
  streaming,
  onAnimatingChange,
  plain,
  variant = "text-regular",
  color,
  resolveFileHref,
}: Props) {
  const { revealed, animating } = useStreamReveal(text, streaming);

  const onAnimatingChangeRef = useRef(onAnimatingChange);
  onAnimatingChangeRef.current = onAnimatingChange;

  // Rows share one callback, so only report transitions this row caused —
  // otherwise a row mounting or unmounting clears the flag for a row that is
  // still animating.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (animating === reportedRef.current) return;
    reportedRef.current = animating;
    onAnimatingChangeRef.current?.(animating);
  }, [animating]);

  useEffect(
    () => () => {
      if (reportedRef.current) onAnimatingChangeRef.current?.(false);
    },
    []
  );

  return (
    <Text variant={variant} style={{ minWidth: 0 }} color={color}>
      {plain ? (
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {revealed}
        </span>
      ) : (
        <MarkdownStyles>
          <Markdown internalHref={resolveFileHref}>{revealed}</Markdown>
        </MarkdownStyles>
      )}
    </Text>
  );
}
